import React, { useState, useEffect } from 'react';

// Date range + drill-down redesign (2026-07-29), per RMS request:
//  1. Stat cards (Total Alerts Sent / Today's Alerts / Total Clients Alerted /
//     Channels Used) removed, replaced with a From/To date range that
//     defaults to today (IST) on load.
//  2. Recipients count is now a clickable link that opens the per-client
//     drill-down (Mobile, Client Code, Assigned On, Email Status, WhatsApp
//     Status, Updated On) in a new browser tab.
//
// Note: this bypasses the existing getCommunications() service helper and
// calls the API directly with fetch -- its signature/params weren't
// available to build against, and a raw fetch (same pattern BFUpload.jsx
// already uses elsewhere in this app) avoids guessing at it. If
// services/api.js's getCommunications already supports a params argument,
// swapping this back to use it is a one-line change.
function getISTTodayDate() {
  const now    = new Date();
  const istNow = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return istNow.toISOString().slice(0, 10);
}

export default function ClientAlerts() {
  const today = getISTTodayDate();

  const [comms,     setComms]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState(null);
  const [fromDate,  setFromDate]  = useState(today);
  const [toDate,    setToDate]    = useState(today);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadComms = async (from = fromDate, to = toDate) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nb_admin_token');
      const params = new URLSearchParams({ from, to });
      const res  = await fetch(`/api/admin/control/communications?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed');
      setComms(data.communications || []);
    } catch { showToast('Failed to load communication history.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadComms(today, today); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const EXCHANGE_STYLE = {
    NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  };

  // Opens the per-recipient drill-down for one alert campaign in a new
  // browser tab. The admin's bearer token has to travel as a query param
  // here (a plain window.open()/new-tab navigation can't carry a custom
  // Authorization header the way the SPA's own fetch calls do) -- the new
  // backend route verifies it the same way, just read from ?token= instead
  // of the header. Acceptable for an internal RMS tool; flagging it since
  // it does mean the token is briefly visible in the tab's URL/history.
  const openRecipients = (commId) => {
    const token = localStorage.getItem('nb_admin_token');
    const url = `/api/admin/control/communications/${commId}/recipients/view?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif" }}>

      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: toast.type === 'error' ? '#fff5f5' : '#f0fdf4',
          border: `0.5px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          color: toast.type === 'error' ? '#dc2626' : '#15803d',
          padding: '10px 18px', borderRadius: 8, fontSize: 13,
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}>{toast.msg}</div>
      )}

      {/* Date range bar (replaces the old stat cards) */}
      <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>From</div>
          <input type="date" value={fromDate} max={toDate}
            onChange={e => setFromDate(e.target.value)}
            style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0', fontFamily: 'inherit' }} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>To</div>
          <input type="date" value={toDate} min={fromDate}
            onChange={e => setToDate(e.target.value)}
            style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0', fontFamily: 'inherit' }} />
        </div>
        <button onClick={() => loadComms(fromDate, toDate)}
          style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Apply
        </button>
        <button onClick={() => { setFromDate(today); setToDate(today); loadComms(today, today); }}
          style={{ background: 'transparent', color: '#64748b', border: '0.5px solid #e2e8f0', padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          Today
        </button>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
          {comms.length} alert{comms.length !== 1 ? 's' : ''} · {comms.reduce((a, c) => a + (c.recipients_count || 0), 0)} clients alerted in range
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `0.5px solid ${'#e2e8f0'}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Communication History</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>All bulk alerts sent to clients via Email and WhatsApp</div>
          </div>
          <button onClick={() => loadComms(fromDate, toDate)} style={{ background: 'transparent', border: '0.5px solid #e2e8f0', color: '#64748b', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
            ↻ Refresh
          </button>
        </div>

        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '60px 100px 80px 80px 120px 1fr 120px 100px', padding: '10px 20px', background: '#f8fafc', borderBottom: `0.5px solid ${'#e2e8f0'}` }}>
          {['#', 'Sent By', 'Exchange', 'Segment', 'Channel', 'Message', 'Recipients', 'Sent At'].map(h => (
            <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading...</div>
        ) : comms.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 14, color: '#64748b' }}>No communications in this date range.</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
              Go to Segment Control → Alert Clients to send bulk notifications.
            </div>
          </div>
        ) : (
          comms.map((c, i) => {
            const exStyle = EXCHANGE_STYLE[c.exchange] || { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
            return (
              <div key={c.comm_id} style={{
                display: 'grid', gridTemplateColumns: '60px 100px 80px 80px 120px 1fr 120px 100px',
                padding: '13px 20px', alignItems: 'center',
                borderBottom: i < comms.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                background: i%2===0?('#fff'):('#fafafa')
              }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>#{c.comm_id}</div>

                <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>{c.sent_by || 'Admin'}</div>

                <div>
                  {c.exchange ? (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: exStyle.bg, color: exStyle.color, border: `0.5px solid ${exStyle.border}` }}>
                      {c.exchange}
                    </span>
                  ) : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                </div>

                <div style={{ fontSize: 11, color: '#64748b' }}>{c.segment || '—'}</div>

                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe' }}>
                    ✉ Email
                  </span>
                  <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#f0fdf4', color: '#15803d', border: '0.5px solid #bbf7d0' }}>
                    💬 WA
                  </span>
                </div>

                <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>
                  {c.message_content || '—'}
                </div>

                <div>
                  <button
                    onClick={() => openRecipients(c.comm_id)}
                    title="View per-client delivery details"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#eff6ff', color: '#1d4ed8', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {c.recipients_count || 0}
                    </div>
                    <span style={{ fontSize: 11, color: '#2563eb', textDecoration: 'underline' }}>clients</span>
                  </button>
                </div>

                <div style={{ fontSize: 10, color: '#64748b' }}>
                  {new Date(c.sent_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {comms.length > 0 && (
        <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
          {comms.length} records
        </div>
      )}
    </div>
  );
}